import { BaseHandle } from '../base/baseHandle'
import { arePointsWithinDistance } from '../../helperFunctions'
import { Point } from '../../types'
import { CircleHandle } from '../handles/circleHandle'
import { MARKER_DEFAULT_STROKE_COLOR } from '../../constants'

interface ControlPointPositions {
  start?: Point
  end?: Point
}

class BezierAdjustmentHandle extends BaseHandle {
  _size: number
  _controlPointPositions: ControlPointPositions
  _strokeWeight: number
  strokeColor: string
  anchorHandle: CircleHandle
  startControlPointHandle: CircleHandle
  endControlPointHandle: CircleHandle
  pressedHandles: Array<BaseHandle>
  snapRadius: number

  constructor(p, position: Point, controlPointPositions: ControlPointPositions, size: number, strokeWeight: number) {
    super(p)
    this.position = position
    this.size = size || 5
    this.pressedHandles = []
    this._controlPointPositions = controlPointPositions
    this._strokeWeight = strokeWeight
    this.strokeColor = MARKER_DEFAULT_STROKE_COLOR
    this.snapRadius = 5
    this.populateHandles()
  }

  populateAnchorHandle() {
    this.anchorHandle = new CircleHandle(this.p, this.position, this.size, this.strokeWeight)
  }

  populateStartControlPointHandle() {
    if (this.controlPointPositions.start) {
      this.startControlPointHandle = new CircleHandle(
        this.p,
        this.controlPointPositions.start,
        this.size,
        this.strokeWeight
      )
    }
  }

  populateEndControlPointHandle() {
    if (this.controlPointPositions.end) {
      this.endControlPointHandle = new CircleHandle(
        this.p,
        this.controlPointPositions.end,
        this.size,
        this.strokeWeight
      )
    }
  }

  populateHandles() {
    this.populateAnchorHandle()
    this.populateStartControlPointHandle()
    this.populateEndControlPointHandle()
  }

  /**
   * Moves the entire anchor point and any associated control points by the delta x and delta y amounts.
   * @param deltaX Change in X coordinate
   * @param deltaY Change in Y coodindate
   */
  moveAnchor(deltaX: number, deltaY: number): void {
    this.position.x += deltaX
    this.position.y += deltaY
    if (this.controlPointPositions.start) {
      this.controlPointPositions.start.x += deltaX
      this.controlPointPositions.start.y += deltaY
    }
    if (this.controlPointPositions.end) {
      this.controlPointPositions.end.x += deltaX
      this.controlPointPositions.end.y += deltaY
    }
  }

  moveControlPoint(deltaX: number, deltaY: number, controlPoint: Point) {
    const newPosition = {
      x: controlPoint.x + deltaX,
      y: controlPoint.y + deltaY,
    }

    const isWithinAnchorRadius = arePointsWithinDistance(newPosition, this.position, this.snapRadius)

    //TODO add extra flag that tracks if control points have been moved outside
    //the sticky radius to begin with. As current implementation
    //makes that handles "sticky" when in original position

    if (isWithinAnchorRadius) {
      controlPoint.x = this.position.x
      controlPoint.y = this.position.y
    } else {
      controlPoint.x = newPosition.x
      controlPoint.y = newPosition.y
    }
  }

  moveStartControlPoint(deltaX: number, deltaY: number) {
    this.moveControlPoint(deltaX, deltaY, this.controlPointPositions.start)
  }

  moveEndControlPoint(deltaX: number, deltaY: number) {
    this.moveControlPoint(deltaX, deltaY, this.controlPointPositions.end)
  }

  moveBothControlPoints(deltaX: number, deltaY: number) {
    this.moveStartControlPoint(deltaX, deltaY)
    this.moveEndControlPoint(-deltaX, -deltaY)
  }

  getMousedOverHandles() {
    const handles = [this.anchorHandle, this.startControlPointHandle, this.startControlPointHandle]

    return handles.filter((handle) => {
      const isMousedOver = handle?.isMousedOver()
      return isMousedOver
    })
  }

  isMousedOver() {
    const handles = [this.anchorHandle, this.startControlPointHandle, this.endControlPointHandle]
    return handles.some((handle) => handle?.isMousedOver())
  }

  handlePress() {
    this.pressedHandles = this.getMousedOverHandles()
  }

  isStartControlHandleInStartPosition() {
    return (
      this.anchorHandle.position.x === this.startControlPointHandle?.position.x &&
      this.anchorHandle.position.y === this.startControlPointHandle?.position.y
    )
  }

  isEndControlHandleInStartPosition() {
    return (
      this.anchorHandle.position.x === this.endControlPointHandle?.position.x &&
      this.anchorHandle.position.y === this.endControlPointHandle?.position.y
    )
  }

  handleDrag(deltaX: number, deltaY: number) {
    const ctrlDown = this.p.keyIsDown(this.p.CONTROL)
    const isStartControlHandlePressed = this.pressedHandles.includes(this.startControlPointHandle)
    const isEndControlHandlePressed = this.pressedHandles.includes(this.endControlPointHandle)

    const isStartControlHandleInStartPosition = this.isStartControlHandleInStartPosition()

    const isEndControlHandleInStartPosition = this.isEndControlHandleInStartPosition()

    const isBothControlPointsInStartPosition = isStartControlHandleInStartPosition && isEndControlHandleInStartPosition

    //TODO - additional check for if the points are
    //also collinear

    const moveBothControlPoints = ctrlDown && isBothControlPointsInStartPosition
    const moveStartControlPoint = isStartControlHandlePressed && (!isStartControlHandleInStartPosition || ctrlDown)
    const moveEndControlPoint = isEndControlHandlePressed && (!isEndControlHandleInStartPosition || ctrlDown)

    switch (true) {
      case moveBothControlPoints:
        this.moveBothControlPoints(deltaX, deltaY)
        break
      case moveStartControlPoint:
        this.moveStartControlPoint(deltaX, deltaY)
        break
      case moveEndControlPoint:
        this.moveEndControlPoint(deltaX, deltaY)
        break
      default:
        this.moveAnchor(deltaX, deltaY)
        break
    }
  }

  setupStroke() {
    this.p.stroke(this.strokeColor)
    this.p.strokeWeight(this.strokeWeight)
  }

  render() {
    const anchorPosition = this.position
    const controlPointPositions = this.controlPointPositions

    this.setupStroke()

    if (!this.isStartControlHandleInStartPosition() && this.startControlPointHandle) {
      const startControlPoint = controlPointPositions.start
      this.p.line(anchorPosition.x, anchorPosition.y, startControlPoint.x, startControlPoint.y)
      this.startControlPointHandle.render()
    }

    if (!this.isEndControlHandleInStartPosition() && this.endControlPointHandle) {
      const endControlPoint = controlPointPositions.end
      this.p.line(anchorPosition.x, anchorPosition.y, endControlPoint.x, endControlPoint.y)
      this.endControlPointHandle.render()
    }

    this.anchorHandle.render()
  }

  get controlPointPositions() {
    return this._controlPointPositions
  }

  set controlPointPositions(newPositions: ControlPointPositions) {
    const startControlPointPreviouslyDefined = this.controlPointPositions?.start
    const endControlPointPreviouslyDefined = this.controlPointPositions?.end

    this._controlPointPositions = newPositions

    if (!startControlPointPreviouslyDefined) {
      this.populateStartControlPointHandle()
    }
    if (!endControlPointPreviouslyDefined) {
      this.populateEndControlPointHandle()
    }
  }

  get size() {
    return this._size
  }

  set size(newSize: number) {
    this._size = newSize
    const handles = [this.anchorHandle, this.startControlPointHandle, this.endControlPointHandle]
    handles.forEach((handle) => {
      if (handle) {
        handle.size = this.size
      }
    })
  }

  set strokeWeight(newStrokeWeight: number) {
    const handles = [this.anchorHandle, this.startControlPointHandle, this.endControlPointHandle]
    this._strokeWeight = newStrokeWeight
    handles.forEach((handle) => {
      if (handle) {
        handle.strokeWeight = this.strokeWeight
      }
    })
  }

  get strokeWeight(): number {
    return this._strokeWeight
  }
}

export { BezierAdjustmentHandle }
